import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select"
import { Plus, Trash2, ChevronRight } from "lucide-react"
import { GripVertical } from "lucide-react"
import { Section, ToggleField } from "../../shared/components/EditorFields"
import { MENU_ITEM_TYPES, MENU_ITEM_TYPE_LABELS, type MenuItem } from "../types"
import * as db from "../store"

export /**
 * Recursive editor for the header nav tree. Titles are inline locale maps
 * (not a joined translations table — see types.ts's fidelity note 1b), so
 * editing a title edits the whole map's current-locale key directly here.
 * Uses the same entity-wide locale switcher as the rest of the editor
 * (passed in as `locale`) rather than its own picker.
 */
function HeaderMenuEditor({ items, onChange, locale }: { items: MenuItem[]; onChange: (items: MenuItem[]) => void; locale: string }) {
  const update = (path: number[], patch: Partial<MenuItem>) => {
    const next = structuredClone(items) as MenuItem[]
    let list = next
    for (let i = 0; i < path.length - 1; i++) list = list[path[i]].children
    Object.assign(list[path[path.length - 1]], patch)
    onChange(next)
  }
  const remove = (path: number[]) => {
    const next = structuredClone(items) as MenuItem[]
    let list = next
    for (let i = 0; i < path.length - 1; i++) list = list[path[i]].children
    list.splice(path[path.length - 1], 1)
    onChange(next)
  }
  const addChild = (path: number[]) => {
    const next = structuredClone(items) as MenuItem[]
    let list = next
    for (const i of path) list = list[i].children
    list.push(db.emptyMenuItem("link"))
    onChange(next)
  }
  const addTopLevel = () => onChange([...items, db.emptyMenuItem("link")])

  const renderItem = (item: MenuItem, path: number[]): React.ReactNode => (
    <div key={item.id} className="space-y-1.5 rounded border p-2" style={{ marginLeft: (path.length - 1) * 12 }}>
      <div className="flex items-center gap-1.5">
        <GripVertical className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <Select value={item.type} onValueChange={v => update(path, { type: v as MenuItem["type"] })}>
          <SelectTrigger className="h-7 w-[100px] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {MENU_ITEM_TYPES.map(t => (
              <SelectItem key={t} value={t} className="text-xs">
                {MENU_ITEM_TYPE_LABELS[t]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={item.title?.[locale] ?? ""}
          onChange={e => update(path, { title: { ...item.title, [locale]: e.target.value } })}
          placeholder={`Title (${locale})`}
          className="h-7 flex-1 text-xs"
        />
        <button type="button" className="shrink-0 text-muted-foreground hover:text-destructive" onClick={() => remove(path)} title="Remove">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      {item.type !== "menu" && item.type !== "services" && item.type !== "bot" && item.type !== "language" && (
        <Input
          value={item.link ?? ""}
          onChange={e => update(path, { link: e.target.value })}
          placeholder="Link URL"
          className="h-7 text-xs"
        />
      )}
      {item.type === "menu" && (
        <>
          <ToggleField label="Mega menu" hint="Renders as a dropdown of the children below" checked={!!item.megamenu} onChange={v => update(path, { megamenu: v })} />
          <div className="space-y-1.5">
            {item.children.map((child, i) => renderItem(child, [...path, i]))}
            <Button variant="ghost" size="sm" className="h-6 text-[11px]" onClick={() => addChild(path)}>
              <Plus className="mr-1 h-3 w-3" /> Add child link
            </Button>
          </div>
        </>
      )}
    </div>
  )

  return (
    <Section title={`Header nav (${db.flattenMenu(items).length})`}>
      <p className="text-[11px] text-muted-foreground">
        Titles shown/edited below are for {locale} — use the locale switcher above to change languages.
      </p>
      <div className="space-y-1.5">
        {items.map((item, i) => renderItem(item, [i]))}
      </div>
      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addTopLevel}>
        <Plus className="mr-1 h-3.5 w-3.5" /> Add nav item
      </Button>
    </Section>
  )
}

// ── Footer ───────────────────────────────────────────────────────────────────

